(() => {
  if (window.gpCinematicBootstrapped) return;
  window.gpCinematicBootstrapped = true;

  const ROOT_SELECTOR = '[data-gp-cinematic]';
  const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [contenteditable="true"]';

  const initialize = (root) => {
    if (!root || root.dataset.gpCinematicReady === 'true') return;

    const slides = [...root.querySelectorAll('[data-gp-cinematic-slide]')];
    if (!slides.length) return;

    root.dataset.gpCinematicReady = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const autoplayEnabled = root.dataset.autoplay === 'true';
    const slideDuration = Math.max(3000, Number(root.dataset.slideDuration) || 6000);
    const slideCount = slides.length;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let autoplayTimer = null;
    let introPending = document.documentElement.classList.contains('gp-intro-pending');
    let introObserver = null;
    let pointerStart = null;

    const getVisibleShell = (slide) => {
      const selector = mobileQuery.matches ? '.gp-cinematic__media--mobile' : '.gp-cinematic__media--desktop';
      return slide.querySelector(selector);
    };

    const markShellLoaded = (shell) => {
      shell?.classList.add('is-loaded');
    };

    const hydrateImage = (shell, image, isFirst) => {
      if (!image || image.dataset.gpHydrated === 'true') return;
      image.dataset.gpHydrated = 'true';
      image.fetchPriority = isFirst ? 'high' : 'auto';
      image.loading = isFirst ? 'eager' : 'lazy';
      image.addEventListener('load', () => markShellLoaded(shell), { once: true, signal });
      image.addEventListener('error', () => markShellLoaded(shell), { once: true, signal });
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.src) image.src = image.dataset.src;
      if (image.complete) markShellLoaded(shell);
    };

    const hydrateVideo = (shell, video) => {
      if (!video) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      if (video.dataset.poster) {
        shell.style.backgroundImage = `url("${video.dataset.poster}")`;
        video.poster = video.dataset.poster;
      }

      if (video.dataset.gpHydrated !== 'true') {
        video.dataset.gpHydrated = 'true';
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.addEventListener('playing', () => markShellLoaded(shell), { signal });
        video.addEventListener('loadeddata', () => markShellLoaded(shell), { once: true, signal });
        video.addEventListener('error', () => markShellLoaded(shell), { once: true, signal });
        video.load();
      }

      if (reducedMotionQuery.matches || document.hidden) {
        video.pause();
        markShellLoaded(shell);
        return;
      }

      video.play().then(() => markShellLoaded(shell)).catch(() => markShellLoaded(shell));
    };

    const hydrateSlide = (index) => {
      const slide = slides[index];
      const shell = getVisibleShell(slide);
      if (!shell) return;
      const image = shell.querySelector('[data-gp-cinematic-image]');
      const video = shell.querySelector('[data-gp-cinematic-video]');
      if (image) hydrateImage(shell, image, index === 0);
      if (video) hydrateVideo(shell, video);
      if (!image && !video && shell.style.backgroundImage) markShellLoaded(shell);
    };

    const stopInactiveVideos = () => {
      slides.forEach((slide, index) => {
        if (index === activeIndex) return;
        slide.querySelectorAll('video').forEach((video) => {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (_error) {
            // Some browsers disallow seeking until metadata exists.
          }
        });
      });
    };

    const syncIndicator = () => {
      const current = String(activeIndex + 1).padStart(2, '0');
      const total = String(slideCount).padStart(2, '0');
      root.querySelectorAll('[data-gp-current-slide]').forEach((element) => {
        element.textContent = current;
      });
      root.querySelectorAll('[data-gp-total-slides]').forEach((element) => {
        element.textContent = total;
      });
      root.querySelectorAll('[data-gp-cinematic-progress]').forEach((progress) => {
        progress.style.left = `${(activeIndex / slideCount) * 100}%`;
        progress.style.width = `${100 / slideCount}%`;
      });
    };

    const clearAutoplay = () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      clearAutoplay();
      if (
        !autoplayEnabled
        || slideCount < 2
        || reducedMotionQuery.matches
        || document.hidden
        || document.documentElement.classList.contains('gp-intro-pending')
      ) return;
      autoplayTimer = window.setTimeout(() => {
        autoplayTimer = null;
        if (document.documentElement.classList.contains('gp-intro-pending')) return;
        goTo(activeIndex + 1, false);
      }, slideDuration);
    };

    const goTo = (requestedIndex, userInitiated = true) => {
      const nextIndex = ((requestedIndex % slideCount) + slideCount) % slideCount;
      if (nextIndex === activeIndex) {
        if (userInitiated) scheduleAutoplay();
        return;
      }

      activeIndex = nextIndex;
      hydrateSlide(activeIndex);
      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      stopInactiveVideos();
      syncIndicator();
      scheduleAutoplay();
    };

    introObserver = new MutationObserver(() => {
      const nextIntroPending = document.documentElement.classList.contains('gp-intro-pending');
      if (nextIntroPending === introPending) return;

      introPending = nextIntroPending;
      if (introPending) {
        clearAutoplay();
      } else {
        scheduleAutoplay();
      }
    });
    introObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    root.querySelectorAll('[data-gp-cinematic-track]').forEach((track) => {
      track.addEventListener('click', (event) => {
        const bounds = track.getBoundingClientRect();
        const ratio = Math.min(0.999, Math.max(0, (event.clientX - bounds.left) / bounds.width));
        goTo(Math.floor(ratio * slideCount));
      }, { signal });
    });

    document.addEventListener('keydown', (event) => {
      if (event.defaultPrevented || event.target?.closest?.(INTERACTIVE_SELECTOR)) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(activeIndex + 1);
      }
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.target?.closest?.(INTERACTIVE_SELECTOR)) return;
      pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }, { passive: true, signal });

    document.addEventListener('pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const horizontalDistance = event.clientX - pointerStart.x;
      const verticalDistance = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(horizontalDistance) < 50 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;
      goTo(activeIndex + (horizontalDistance < 0 ? 1 : -1));
    }, { passive: true, signal });

    document.addEventListener('pointercancel', () => {
      pointerStart = null;
    }, { passive: true, signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearAutoplay();
        slides[activeIndex].querySelectorAll('video').forEach((video) => video.pause());
      } else {
        hydrateSlide(activeIndex);
        scheduleAutoplay();
      }
    }, { signal });

    mobileQuery.addEventListener('change', () => {
      slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
      hydrateSlide(activeIndex);
    }, { signal });

    reducedMotionQuery.addEventListener('change', () => {
      if (reducedMotionQuery.matches) {
        clearAutoplay();
        slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
      } else {
        hydrateSlide(activeIndex);
        scheduleAutoplay();
      }
    }, { signal });

    root.gpCinematicGoToBlock = (blockId) => {
      const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index >= 0) goTo(index);
    };

    root.gpCinematicDestroy = () => {
      clearAutoplay();
      introObserver?.disconnect();
      controller.abort();
      slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()));
      delete root.gpCinematicGoToBlock;
      delete root.gpCinematicDestroy;
    };

    hydrateSlide(activeIndex);
    stopInactiveVideos();
    syncIndicator();
    scheduleAutoplay();
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  initializeAll();

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector?.(ROOT_SELECTOR)?.gpCinematicDestroy?.();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR) || document.querySelector(`${ROOT_SELECTOR}[data-section-id="${event.detail.sectionId}"]`);
    root?.gpCinematicGoToBlock?.(event.detail.blockId);
  });
})();
